/**
 * /my-coaching/business/nova/assets/$assetKey — one asset's page. Opening it reads
 * only; the actions on the page are what start work.
 *
 * A content collection has no page here: My content is the one list of its
 * items, so its key redirects there under its type. Its items keep their own
 * pages below this route.
 */

import { createFileRoute, redirect } from "@tanstack/react-router";

import { AssetPage } from "~/components/nova/assets/asset-page";
import {
  novaAssetDetailQueryOptions,
  novaAssetsQueryOptions,
} from "~/lib/queries/novaAssetsQuery";
import { isContentAssetKey } from "~/lib/shared/nova/assets/catalog";

export const Route = createFileRoute(
  "/_user/my-coaching/business/nova/assets/$assetKey/",
)({
  // The loader's asset label replaces this, as on the layout above.
  staticData: { title: "Asset" },
  beforeLoad: ({ context }) => {
    const { key } = context.asset;
    if (isContentAssetKey(key)) {
      throw redirect({
        to: "/my-coaching/business/nova/content",
        search: { type: key },
        replace: true,
      });
    }
  },
  loader: async ({ context }) => {
    const { asset } = context;
    const userId = context.user.clerkUserId;
    // Prefetches for SSR only, as on the list: the page reads both queries
    // itself and renders its own error state, so a failure here must not
    // reach the router's error boundary.
    await Promise.all([
      context.queryClient
        .ensureQueryData(novaAssetsQueryOptions(userId))
        .catch(() => undefined),
      context.queryClient
        .ensureQueryData(novaAssetDetailQueryOptions(userId, asset.key))
        .catch(() => undefined),
    ]);
    return { title: asset.label, assetKey: asset.key, userId };
  },
  component: AssetRoute,
});

function AssetRoute() {
  const { assetKey, userId } = Route.useLoaderData();
  return <AssetPage assetKey={assetKey} userId={userId} />;
}
